import { useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { AccentButton, AuthScene, Button, LoadingState } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT } from "../../i18n/I18nProvider";
import { ApiError } from "../../lib/api";
import { logout } from "../../lib/auth";
import { consentApi, type ConsentRequest } from "../../lib/aiConnections";
import { Logo } from "../../components/Logo";
import { AppBackground } from "../../components/AppBackground";
import { LanguageMenu } from "../../components/LanguageMenu";
import { ThemeToggle } from "../../components/ThemeToggle";
import { ConsentView } from "./ConsentView";
import { ConnectNotice } from "./ConnectNotice";
import { hasAuthorizeQuery, queryObject, refusalCopy } from "./lib";

type View =
  | { kind: "direct" }
  | { kind: "loading" }
  | { kind: "loadFailed" }
  | { kind: "ready"; request: ConsentRequest; email: string }
  | { kind: "refused"; error: string; description: string; redirect?: string };

/**
 * `/connect` - where an AI app (Claude's "Add custom connector" and the like)
 * sends the person to allow or refuse access to their Husly account. Signed-in
 * only: main.tsx shows the login page on this same address first.
 *
 * The address is the request. It goes to the API unchanged to be read, and back
 * as plain fields with the answer; both answers come back as an address in the
 * app, which the browser then follows.
 */
export function ConnectPage({ authSpec, onSignedOut }: { authSpec?: AuthLayoutSpec; onSignedOut: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  const glow = authSpec?.glow ?? "aurora";

  const [view, setView] = useState<View>(() => (hasAuthorizeQuery(search) ? { kind: "loading" } : { kind: "direct" }));
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<"allow" | "deny" | "switch" | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  useEffect(() => {
    if (!hasAuthorizeQuery(search)) {
      setView({ kind: "direct" });
      return;
    }
    let live = true;
    setView({ kind: "loading" });
    consentApi.read(search).then(
      (r) => {
        if (!live) return;
        setView(
          r.ok
            ? { kind: "ready", request: r.request, email: r.user.email }
            : { kind: "refused", error: r.error, description: r.description, redirect: r.redirect },
        );
      },
      (err) => {
        if (!live) return;
        // The session ended in between: back to the login page on this address.
        if (err instanceof ApiError && err.status === 401) onSignedOut();
        else setView({ kind: "loadFailed" });
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, attempt]);

  async function answer(allow: boolean) {
    if (busy) return;
    setBusy(allow ? "allow" : "deny");
    setSendFailed(false);
    try {
      const { redirect } = await consentApi.answer(queryObject(search), allow);
      // The buttons stay locked: the page is leaving.
      setLeaving(true);
      window.location.assign(redirect);
    } catch (err) {
      setBusy(null);
      if (err instanceof ApiError && err.status === 401) return onSignedOut();
      const body = err instanceof ApiError ? (err.body as { error?: string; description?: string } | null) : null;
      if (err instanceof ApiError && err.status === 400 && body?.error) {
        setView({ kind: "refused", error: body.error, description: body.description ?? "" });
        return;
      }
      setSendFailed(true);
    }
  }

  // Signing out keeps this address, so the login page that follows comes back here.
  async function switchAccount() {
    setBusy("switch");
    await logout().catch(() => {});
    onSignedOut();
  }

  // Quiet next to a main action, an outline button when it is the only way out.
  const openAccount = (alone: boolean) => (
    <Button
      type="button"
      variant={alone ? "outline" : "ghost"}
      size={alone ? "lg" : "md"}
      className="w-full"
      onClick={() => navigate("/", { replace: true })}
    >
      {t("connect.openAccount")}
    </Button>
  );

  let body: ReactNode;
  if (view.kind === "loading") {
    body = <LoadingState label={t("connect.loading")} className="min-h-40 py-8" />;
  } else if (view.kind === "ready") {
    body = (
      <ConsentView
        request={view.request}
        email={view.email}
        busy={busy}
        leaving={leaving}
        sendFailed={sendFailed}
        glow={glow}
        onAnswer={answer}
        onSwitchAccount={switchAccount}
      />
    );
  } else if (view.kind === "refused") {
    const copy = refusalCopy(view.error);
    const back = view.redirect;
    body = (
      <ConnectNotice
        tone="error"
        title={t("connect.errorTitle")}
        body={t(copy.key)}
        detail={copy.showReason && view.description ? t("connect.reason", { text: view.description }) : null}
        actions={
          <>
            {back && (
              <AccentButton glow={glow} type="button" size="lg" className="w-full" wrapperClassName="w-full" onClick={() => window.location.assign(back)}>
                {t("connect.backToApp")}
              </AccentButton>
            )}
            {openAccount(!back)}
          </>
        }
      />
    );
  } else if (view.kind === "loadFailed") {
    body = (
      <ConnectNotice
        tone="error"
        title={t("connect.directTitle")}
        body={t("connect.loadFailed")}
        actions={
          <>
            <AccentButton glow={glow} type="button" size="lg" className="w-full" wrapperClassName="w-full" onClick={() => setAttempt((n) => n + 1)}>
              {t("connect.retry")}
            </AccentButton>
            {openAccount(false)}
          </>
        }
      />
    );
  } else {
    body = (
      <ConnectNotice
        tone="info"
        title={t("connect.directTitle")}
        body={t("connect.directBody")}
        actions={openAccount(true)}
      />
    );
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <LanguageMenu />
        </div>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-5 shadow-soft sm:p-6">{body}</div>
      </motion.div>
    </AuthScene>
  );
}
